import React, { useEffect, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import ConnectWalletButton from "./ConnectModal";
import BrandLogo from "../BrandLogo";
import ErrorBoundary from "../ErrorBoundary";

/**
 * Global top navigation — mission-control bar from the NiftySky design.
 * Fixed to the top, condenses on scroll. Desktop links/connect show ≥920px;
 * below that a hamburger opens a right-side sheet (close button + scrim +
 * Escape all dismiss it). Keeps the thirdweb wallet connect throughout.
 */
const NAV_LINKS = [
  { text: "Marketplace", href: "/marketplace" },
  { text: "Planets", href: "/real-estate/categories/planets" },
  { text: "Moons", href: "/real-estate/categories/moons" },
  { text: "Roadmap", href: "/roadmap" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const router = useRouter();

  useEffect(() => {
    const f = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", f);
    f();
    return () => window.removeEventListener("scroll", f);
  }, []);

  // Close on route change.
  useEffect(() => {
    const close = () => setMenuOpen(false);
    router.events.on("routeChangeStart", close);
    return () => router.events.off("routeChangeStart", close);
  }, [router]);

  // Close on Escape + lock body scroll while open.
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const isActive = (href: string) =>
    href === "/" ? router.pathname === "/" : router.pathname.startsWith(href);

  return (
    <nav className={"nav" + (scrolled ? " scrolled" : "")}>
      <Link href="/" className="brand" aria-label="NiftySky home">
        <BrandLogo size={34} />
        <span className="brand-name">Nifty<b>Sky</b></span>
      </Link>

      {/* Desktop links */}
      <div className="nav-links">
        {NAV_LINKS.map((l) => (
          <Link key={l.href} href={l.href}>
            {l.text}
          </Link>
        ))}
      </div>

      <div className="nav-right">
        <div className="net-pill">
          <span className="dot" />
          BASE SEPOLIA
        </div>
        <div className="nav-connect-desktop">
          <ErrorBoundary
            resetKey={router.asPath}
            fallback={
              <button type="button" className="net-pill" onClick={() => window.location.reload()}>
                RECONNECT
              </button>
            }
          >
            <ConnectWalletButton />
          </ErrorBoundary>
        </div>

        {/* Mobile hamburger (only < 920px) */}
        <button
          type="button"
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
          aria-controls="ns-mobile-sheet"
          onClick={() => setMenuOpen((v) => !v)}
          className="ns-burger"
        >
          <span />
          <span />
          <span />
        </button>
      </div>

      {/* Mobile scrim — click anywhere outside closes the sheet */}
      <div
        className={"ns-mobile-scrim" + (menuOpen ? " open" : "")}
        onClick={() => setMenuOpen(false)}
        aria-hidden
      />

      {/* Mobile sheet */}
      <aside
        id="ns-mobile-sheet"
        className={"ns-mobile-sheet" + (menuOpen ? " open" : "")}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        <div className="ns-sheet-head">
          <span className="brand-name" style={{ fontSize: 16 }}>Nifty<b>Sky</b></span>
          <button
            type="button"
            className="ns-sheet-close"
            aria-label="Close menu"
            onClick={() => setMenuOpen(false)}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>

        <div className="ns-mobile-links">
          {NAV_LINKS.map((l) => (
            <Link
              key={l.href}
              href={l.href}
              className={"ns-mobile-link mono" + (isActive(l.href) ? " active" : "")}
              onClick={() => setMenuOpen(false)}
            >
              <span className="ns-dot" />
              <span className="ns-label">{l.text}</span>
              <span className="ar">→</span>
            </Link>
          ))}
        </div>

        <div className="ns-mobile-foot">
          <div className="ns-mobile-connect">
            <ErrorBoundary
              resetKey={router.asPath}
              fallback={
                <button type="button" className="net-pill" onClick={() => window.location.reload()}>
                  RECONNECT
                </button>
              }
            >
              <ConnectWalletButton />
            </ErrorBoundary>
          </div>
          <div className="ns-mobile-net mono">
            <span className="dot" /> BASE SEPOLIA · ERC-721
          </div>
        </div>
      </aside>

      <style jsx>{`
        /* ── Desktop connect: only ≥920px (matches .nav-links breakpoint) ── */
        .nav-connect-desktop { display: block; }

        /* ── Hamburger: hidden by default; only shown < 920px ── */
        .ns-burger { display: none; }
        @media (max-width: 919px) {
          .ns-burger {
            display: inline-flex;
            flex-direction: column;
            justify-content: center;
            gap: 4px;
            width: 40px;
            height: 40px;
            padding: 10px;
            border: 1px solid var(--hair-2);
            border-radius: 2px;
            background: rgba(255, 255, 255, 0.015);
            cursor: pointer;
          }
          .nav-connect-desktop { display: none; }
        }
        .ns-burger span {
          display: block;
          height: 1.5px;
          width: 100%;
          background: var(--ink-soft);
          transition: 0.3s var(--ease);
        }

        /* ── Scrim + sheet exist only on mobile ── */
        .ns-mobile-scrim,
        .ns-mobile-sheet { display: none; }
        @media (max-width: 919px) {
          .ns-mobile-scrim {
            display: block;
            position: fixed;
            inset: 0;
            z-index: 75;
            background: rgba(2, 3, 8, 0.72);
            -webkit-backdrop-filter: blur(6px);
            backdrop-filter: blur(6px);
            opacity: 0;
            pointer-events: none;
            transition: opacity 0.3s var(--ease);
          }
          .ns-mobile-scrim.open { opacity: 1; pointer-events: auto; }

          .ns-mobile-sheet {
            display: flex;
            position: fixed;
            top: 0;
            right: 0;
            z-index: 80;
            height: 100dvh;
            width: min(86vw, 330px);
            background: linear-gradient(180deg, #070b1c, #04060f);
            border-left: 1px solid var(--hair-2);
            box-shadow: -20px 0 60px -20px rgba(0, 0, 0, 0.8);
            padding: 18px 18px 24px;
            flex-direction: column;
            transform: translateX(100%);
            transition: transform 0.4s var(--ease);
            overscroll-behavior: contain;
          }
          .ns-mobile-sheet.open { transform: none; }
        }

        .ns-sheet-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 4px 4px 16px;
          border-bottom: 1px solid var(--hair);
          margin-bottom: 14px;
        }
        .ns-sheet-close {
          width: 38px;
          height: 38px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border: 1px solid var(--hair);
          border-radius: 2px;
          background: var(--panel);
          color: var(--ink-soft);
          cursor: pointer;
          transition: 0.2s var(--ease);
        }
        .ns-sheet-close:hover { border-color: var(--accent); color: var(--accent-bright); }

        .ns-mobile-links { display: flex; flex-direction: column; gap: 4px; }
        .ns-mobile-link {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 15px 14px;
          font-size: 13px;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: var(--ink-soft);
          text-decoration: none;
          border: 1px solid transparent;
          border-radius: 2px;
          transition: 0.2s var(--ease);
        }
        .ns-mobile-link:hover { color: var(--accent-bright); border-color: var(--hair); background: var(--panel); }
        .ns-mobile-link.active { color: var(--accent-bright); border-color: color-mix(in oklch, var(--accent) 40%, transparent); background: var(--panel-2); }
        .ns-mobile-link .ns-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--hair-2); flex-shrink: 0; transition: 0.2s var(--ease); }
        .ns-mobile-link.active .ns-dot { background: var(--accent); box-shadow: 0 0 8px var(--accent); }
        .ns-mobile-link .ns-label { flex: 1; }
        .ns-mobile-link .ar { color: var(--accent); opacity: 0.55; transition: transform 0.2s var(--ease); }
        .ns-mobile-link:hover .ar { transform: translateX(3px); opacity: 1; }

        .ns-mobile-foot { margin-top: auto; padding-top: 18px; border-top: 1px solid var(--hair); display: flex; flex-direction: column; gap: 14px; }
        .ns-mobile-net { display: flex; align-items: center; gap: 8px; font-size: 10.5px; letter-spacing: 0.12em; color: var(--ink-mute); }
        .ns-mobile-net .dot { width: 6px; height: 6px; border-radius: 50%; background: var(--sp-green); box-shadow: 0 0 8px var(--sp-green); }
      `}</style>
    </nav>
  );
};

export default Navbar;
